import {
  Form,
  FormField,
  FormInput,
  FormSelect,
  Button,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { NOMINEE_RELATIONSHIP } from "creso-core/constants";
import {
  useDistributorNomineeDetails,
  useUpsertDistributorNominee,
} from "creso-core/hooks";
import { handleError } from "@/utils/error";

const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  relationship: z.string().min(1, { message: "Relationship is required" }),
});

type FormValues = z.infer<typeof formSchema>;

export function NomineeDetails({ onComplete }: { onComplete: () => void }) {
  const { data: nomineeDetails } = useDistributorNomineeDetails();
  const upsertDistributorNomineeDetails = useUpsertDistributorNominee();

  const form = useForm({
    resolver: zodResolver(formSchema),
    values: {
      name: nomineeDetails?.name ?? "",
      relationship: nomineeDetails?.relationship ?? "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await upsertDistributorNomineeDetails.mutateAsync({
        name: values.name,
        relationship: values.relationship,
      });
      onComplete();
    } catch (error) {
      handleError(error, "Failed to update nominee details");
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="flex flex-col gap-5 size-full">
      <span className="text-lg font-semibold">Nominee Details</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-5"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-6 pb-8">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormInput
                  label="Name"
                  placeholder="Nominee name"
                  {...field}
                  className="gap-2"
                />
              )}
            />
            <FormField
              control={form.control}
              name="relationship"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Relationship"
                  placeholder="Select"
                  options={NOMINEE_RELATIONSHIP}
                  className="gap-2"
                />
              )}
            />
          </div>

          <div className="flex gap-5">
            <Button
              size="lg"
              type="button"
              disabled={form.formState.isSubmitting}
              onClick={handleSkip}
              variant="outline"
            >
              Skip
            </Button>
            <Button size="lg" className="flex-1" type="submit">
              Continue
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
